import {
  IAppUpdate,
  ICostingList,
  IEventInformationData,
  IFavourite,
  IFavouriteMutation,
  INoteMutation,
  INoteSyncResponse,
  INotesResponse,
  IField,
  IItinerarymap,
  IResponse,
  IValueData,
  OrgListElement,
} from "@eventr/common";
import type { TOtherItinerary } from "../app/itineraries/others/other-itineraries";
import { IExpression } from "../feat/editItinerary/editComponents/arrayFinder";
import { useGuestStore } from "../feat/guest/guest.store";
import { IItinerary } from "../state/interfaces";
import { RocketChannel } from "../state/rocketchat/rocketchat.interface";
import { requestShareCode, requestsItinerary } from "./api";
import { gwApiClient, wpApiClient } from "./apiClients";

export const DEBUG_RENDER_OPTIONS = true;

export interface IUserWithTag {
  type: string;
  option: number;
}

export interface ISection {
  id: number;
  name: string;
  time_created: string;
  time_updated: string;
  properties?: ISectionProperties;
}

interface ISectionProperties {
  jsonName?: string;
  hidden?: boolean;
  sectionDisplayName?: string;
  sectionDisplayNameTeams?: string;
  icon?: string;
  renderer?: string;
  rendererOptions?: object;
  version?: number;
  mobileEditFieldOptions?: {
    components: IEditComponent[];
    headerText?: string;
    icon?: string;
    underButtonText?: string;
    addButtonText?: string;
    deleteButtonText?: string;
    headerKeyValue?: number;
    subPages?: ISubPage[];
  };
}
export interface ISubPage {
  type: string;
  renderOptions?: any;
  headerText?: string;
  fieldId?: number;
  addButtonText?: string;
  components?: IEditComponent[];
  onAddPushTo?: number;
  deleteButtonText?: string;
  userListFieldId?: number;
}
export interface IEditComponent {
  type: string;
  fieldId?: number;
  components?: IEditComponent[];
  settings?: IExpression | any;
  headerText?: string;
  headerIcon?: string;
  iconColour?: string;
  userListFieldId?: number;
  multiBooking?: IMultiBookingParams;
}

interface IMultiBookingParams {
  params: string[];
  format: string;
}

interface IBodyCreateItinerary {
  name: string;
}

export interface IGeneralInfo {
  itinerary_id: number;
  section_id: number;
  value: IEventInformationData;
}

interface ItineraryMapWithValue {
  id: number;
  name: string;
  properties: string;
  time_created: number;
  time_updated: number;
  value: string;
}

const EXPENSE_CRUD_TIMEOUT = 30_000;
const WP_ITIN_BASE_URL = "itinerary/v1/";

const ITINS_BASE_URL = `${WP_ITIN_BASE_URL}itineraries`;
const ENDPOINTS = {
  team: {
    itineraries: {
      copyItinerary: `${ITINS_BASE_URL}/copyItinerary`,
      removePublic: `${ITINS_BASE_URL}/removePublic`,
      updateValues: `${ITINS_BASE_URL}/updateValues`,
    },
    getAPIUpdates: `${WP_ITIN_BASE_URL}apiUpdates/get`,
    todo: {
      upsertTodo: `${WP_ITIN_BASE_URL}todo`,
    },
    values: {
      getAll: (itinId: number) => `${WP_ITIN_BASE_URL}valueData/v2/${itinId}`,
      upsertElement: (itinId: number, sectionId: number) => `${WP_ITIN_BASE_URL}values/upsert/${itinId}/${sectionId}`,
      deleteElement: (itinId: number, sectionId: number) => `${WP_ITIN_BASE_URL}values/delete/${itinId}/${sectionId}`,
    },
    rocketchat: {
      getChannels: (itin_id: number) => `${WP_ITIN_BASE_URL}rocket/getChannels/${itin_id}`,
    },
  },
};

export const DataAPI = {
  getItineraryMap: async (orgID?: string, withHeaders?: boolean): Promise<IItinerarymap[]> => {
    let resp = null;
    if (useGuestStore.getState().isGuestMode && !orgID) {
      await useGuestStore.getState().resolveGuestData(useGuestStore.getState().guestCode);
      resp = [useGuestStore.getState().guestData?.map_entry];
    } else {
      resp = await requestsItinerary.get<IItinerarymap[]>("itineraryMap", {
        ORG_OVERRIDE: orgID,
        WITH_HEADERS: withHeaders,
      });
    }
    // loop  over resp and change id and itinerary_version fro string to number
    if (resp.length)
      resp.forEach((r) => {
        r.id = Number(r.id);
        r.itinerary_version = Number(r.itinerary_version);
      });

    return resp;
  },

  getItineraryMapWithInfo: async (): Promise<ItineraryMapWithValue[]> => {
    const resp = await requestsItinerary.get<ItineraryMapWithValue[]>("itineraries/withDates");
    return resp;
  },

  getData: async (itineraryMapEntry: IItinerarymap): Promise<IItinerary | null> => {
    const path = itineraryMapEntry.itinerary_endpoint.split("v1/")[1];

    let resp: IItinerary | null = null;
    if (useGuestStore.getState().isGuestMode) {
      resp = useGuestStore.getState().guestData?.itinerary ?? null;
    } else if (path !== undefined) {
      resp = await requestsItinerary.get<IItinerary>(path);
    }
    if (DEBUG_RENDER_OPTIONS) {
      const debugResp = resp;

      if (debugResp) {
        debugResp.v2?.forEach((section) => {
          section.rendererOptions = null as any; //dirty but works for now?
        });
        debugResp.version = Number.parseInt(itineraryMapEntry.itinerary_version);
      }
      return debugResp;
    }
    if (resp) {
      resp.version = Number.parseInt(itineraryMapEntry.itinerary_version);
    }
    return resp;
  },

  getSections: async (): Promise<ISection[]> => {
    const resp = await requestsItinerary.get<any>("sections", {
      NOT_IN_GUEST: true,
    });
    resp.forEach((r: any) => {
      if (r.properties) r.properties = JSON.parse(r.properties as string); //someday I will change backend to send an object instead string for now it will do
    });
    return resp;
  },

  getFields: async (section_id?: number): Promise<IField[]> => {
    const res = await requestsItinerary.get<IField[]>("fields/" + (section_id ?? ""), { NOT_IN_GUEST: true });

    res.forEach((r) => {
      r.id = Number.parseInt(r.id as any);
      r.section = Number.parseInt(r.section as any);
      r.position = Number.parseInt(r.position as any);
      if (r.parent) {
        r.parent = Number.parseInt(r.parent as any);
      }
      if (r.type_properties) r.type_properties = JSON.parse(r.type_properties as string); //someday I will change backend to send an object instead string for now it will do
    });
    return res;
  },

  updateSection: async (itinId: number, sectionId: string, body: {}): Promise<any> => {
    const resp = await requestsItinerary.post(`values/updateSection/${itinId}/${sectionId}`, body);
    return resp;
  },

  deleteBooking: async (ids: string, section: string, itinerary: number): Promise<any> => {
    const resp = await requestsItinerary.delete(`values/delete-app/${section}/${itinerary}/${ids}`);
    return resp;
  },

  /// IResponce is not the correct responce type here. The one used in back office might be better but both contain the expected data.
  createItinerary: async (body: IBodyCreateItinerary): Promise<IResponse<{ itineraryID?: number }>> => {
    const resp = await requestsItinerary.post("itineraries/create", body);
    return resp;
  },

  deleteItinerary: async (id: number): Promise<number> => {
    const resp = await requestsItinerary.delete(`itineraries/delete/${id}`);
    return resp;
  },

  /** Every published itinerary in the org the user is not a member of. Needs seeAllItineraries. */
  getOtherItineraries: async (): Promise<TOtherItinerary[]> => {
    return requestsItinerary.get<TOtherItinerary[]>("itineraries/others");
  },

  /** Adds the current user to an itinerary's members. Needs seeAllItineraries. */
  joinItinerary: async (itineraryId: number): Promise<unknown> => {
    return requestsItinerary.post(`itineraries/join/${itineraryId}`, {});
  },

  hideItinerary: async (itineraryId: number): Promise<number[]> => {
    return requestsItinerary.post("user/hidden-itineraries", { itinerary_id: itineraryId });
  },

  unhideItinerary: async (itineraryId: number): Promise<number[]> => {
    return requestsItinerary.delete(`user/hidden-itineraries/${itineraryId}`);
  },

  favourites: {
    getAll: async (): Promise<IFavourite[]> => {
      return requestsItinerary.get<IFavourite[]>("user/favourites", { NOT_IN_GUEST: true });
    },

    /** Sends the whole queue in one request, so a device that has been offline costs one round trip. */
    sync: async (mutations: IFavouriteMutation[]): Promise<IFavourite[]> => {
      return requestsItinerary.post("user/favourites/sync", { mutations });
    },
  },

  notes: {
    get: async (itineraryId: number): Promise<INotesResponse> => {
      return requestsItinerary.get<INotesResponse>(`notes/${itineraryId}`, { NOT_IN_GUEST: true });
    },

    /** Sends every queued change for one itinerary in one request. */
    sync: async (itineraryId: number, mutations: INoteMutation[]): Promise<INoteSyncResponse> => {
      return requestsItinerary.post(`notes/${itineraryId}/sync`, { mutations });
    },
  },

  addTag: async (tagId: number, itineraryId: number) => {
    const resp = await requestsItinerary.post(`itineraryTag/mapping`, {
      itinerary_id: itineraryId,
      tag_id: tagId,
    });
    return resp;
  },

  createUpdateGeneralInfo: async (body: IGeneralInfo): Promise<any> => {
    const resp = await requestsItinerary.post("values/createUpdateGeneralInfo", body);
    return resp;
  },

  getFlightData: async (flightNumber: string, date: string, body: OrgListElement): Promise<any> => {
    const resp = await requestsItinerary.post(`flights/getFlights/${flightNumber}/${date}`, body);
    return resp;
  },

  getShareCodeInfo: async (url: string): Promise<any> => {
    const resp = await requestShareCode.get(url);
    return resp;
  },
  guest: {
    getGuestData: async (code: number): Promise<any> => {
      const resp = await gwApiClient.get(`guest?data=${code}`, {
        NO_AUTH: true,
      });
      return resp;
    },
    duplicateItinerary: async (orgId: string, body: any) => {
      const resp = await wpApiClient.post<any, number>(ENDPOINTS.team.itineraries.copyItinerary, body, {
        ORG_OVERRIDE: orgId,
        timeout: 60000, // this can take a while if the itinerary is large
      });
      return resp;
    },
  },
  getAPIUpdates: async (flights: any[], itinId: number): Promise<IResponse<IAppUpdate>> => {
    const resp = await wpApiClient.post<any, IAppUpdate>(
      ENDPOINTS.team.getAPIUpdates,
      { flights, itinId },
      { NOT_IN_GUEST: true },
    );
    return resp;
  },
  itineraries: {
    updateValue: async (itinId: number): Promise<IResponse<any>> => {
      return await wpApiClient.post(ENDPOINTS.team.itineraries.updateValues, {
        itinId: itinId,
        itin_id: itinId,
      });
    },
    removePublicItin: async (itinId: number, code: string): Promise<IResponse<any>> => {
      return await wpApiClient.post(ENDPOINTS.team.itineraries.removePublic, {
        code,
        itinID: itinId,
      });
    },
  },
  todo: {
    upsertTodo: async (body: TodoUpsert): Promise<IResponse<any>> => {
      return await wpApiClient.post(ENDPOINTS.team.todo.upsertTodo, body);
    },
  },
  editValues: {
    getAll: async (itinId: number): Promise<IResponse<IValueData>> => {
      return await wpApiClient.get<IValueData>(ENDPOINTS.team.values.getAll(itinId));
    },
    upsertElement: async (itinId: number, sectionId: number, element: any): Promise<IResponse<any>> => {
      return await wpApiClient.post<any, any>(
        ENDPOINTS.team.values.upsertElement(itinId, sectionId) + `?origin=app&publish=true`,
        element,
      );
    },
    deleteElement: async (itinId: number, sectionId: number, elementId?: string): Promise<IResponse<any>> => {
      return await wpApiClient.delete(
        ENDPOINTS.team.values.deleteElement(itinId, sectionId) +
          `?origin=app&publish=true${elementId ? "&object_id=" + elementId : ""}`,
      );
    },
  },
  rocketchat: {
    getChannels: async (itin_id: number): Promise<IResponse<RocketChannel[]>> => {
      return await wpApiClient.get(ENDPOINTS.team.rocketchat.getChannels(itin_id), {
        NOT_IN_GUEST: true,
      });
    },
  },
  expenses: {
    getExpenses: async (itin_id: number): Promise<IResponse<ICostingList>> => {
      const resp = await requestsItinerary.get<IResponse<ICostingList>>(`expenses/${itin_id}`);
      return resp;
    },
    addExpenses: async (itin_id: number, new_expenses: ICostingList): Promise<IResponse<ICostingList>> => {
      const resp = await requestsItinerary.post(
        `expenses/add`,
        {
          itinerary_id: itin_id,
          new_expenses: new_expenses,
        },
        {
          timeout: EXPENSE_CRUD_TIMEOUT,
        },
      );
      return resp;
    },
    editExpenses: async (itin_id: number, edited_expenses: ICostingList): Promise<IResponse<ICostingList>> => {
      const resp = await requestsItinerary.post(
        `expenses/edit`,
        {
          itinerary_id: itin_id,
          edited_expenses: edited_expenses,
        },
        {
          timeout: EXPENSE_CRUD_TIMEOUT,
        },
      );
      return resp;
    },
    deleteExpenses: async (itin_id: number, deleted_expenses: ICostingList): Promise<IResponse<ICostingList>> => {
      const resp = await requestsItinerary.post(
        `expenses/delete`,
        {
          itinerary_id: itin_id,
          deleted_expenses: deleted_expenses,
        },
        {
          timeout: EXPENSE_CRUD_TIMEOUT,
        },
      );
      return resp;
    },
  },
};

interface TodoUpsert {
  filler: {
    status: "completed" | "incomplete";
    group_id?: string;
  };
  object_id: string;
  itinerary_id: number;
  filler_id?: number;
  costs?: ICostingList;
}
